import {
  createPopup,
  menu,
  type PopupTarget,
} from '@blocksuite/affine-components/context-menu';
import type { UniComponent } from '@blocksuite/affine-shared/types';
import { cssVarV2 } from '@toeverything/theme/v2';
import { html, render } from 'lit';

import { renderUniLit } from '../../utils/uni-component/uni-component.js';
import { firstGrapheme } from './emoji.js';

export type TagEmojiPickerProps = {
  /** `undefined` means "remove the emoji". */
  onSelect?: (emoji?: string) => void;
  onClose?: () => void;
};

let tagEmojiPicker: UniComponent<TagEmojiPickerProps, any> | undefined;

/** Registered by the host app; without it a plain text input is used. */
export const registerTagEmojiPicker = (
  component: UniComponent<TagEmojiPickerProps, any> | undefined
) => {
  tagEmojiPicker = component;
};

const popTagEmojiPicker = (
  target: PopupTarget,
  onSelect: (emoji: string) => void
) => {
  const component = tagEmojiPicker;
  if (!component) return;
  const wrapper = document.createElement('div');
  wrapper.style.position = 'absolute';
  wrapper.style.backgroundColor = cssVarV2.layer.background.overlayPanel;
  wrapper.style.boxShadow = 'var(--affine-menu-shadow)';
  wrapper.style.borderRadius = '8px';
  const close = createPopup(target, wrapper, {
    onClose: () => render(html``, wrapper),
  });
  render(
    renderUniLit(component, {
      onSelect: emoji => {
        onSelect(emoji ?? '');
        close();
      },
      onClose: close,
    }),
    wrapper
  );
};

/**
 * Menu entry to choose an emoji for a tag option: opens the app's emoji
 * picker when registered, otherwise falls back to a text input.
 */
export const tagEmojiMenuItem = (options: {
  current?: string;
  target: () => PopupTarget;
  onChange: (emoji: string) => void;
}) => {
  if (!tagEmojiPicker) {
    return menu.input({
      placeholder: 'Emoji',
      initialValue: options.current ?? '',
      disableAutoFocus: true,
      onChange: text => options.onChange(firstGrapheme(text)),
    });
  }
  const target = options.target();
  return menu.action({
    name: options.current ? `Emoji: ${options.current}` : 'Add emoji',
    select: () => {
      popTagEmojiPicker(target, options.onChange);
    },
  });
};
